import { ErrorHandler, Injectable, Injector, inject, NgZone } from "@angular/core";
import { Router } from "@angular/router";
import * as Sentry from "@sentry/angular";

/** Stale-deploy signature: a lazy route chunk no longer exists on the server. */
function isChunkLoadError(error: unknown): boolean {
	const message = error instanceof Error ? error.message : String(error);
	return /ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed/i.test(message);
}

/**
 * Global error handler: reports to Sentry (same behavior as
 * `Sentry.createErrorHandler`), then routes unrecoverable failures to the
 * full-screen /error page instead of leaving a silently broken UI.
 *
 * Only chunk-load failures navigate — ordinary runtime errors keep the
 * current view alive and are visible in Sentry via the captured event.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
	// Router must be resolved lazily: injecting it in the constructor creates
	// a cyclic dependency (Router → ... → ErrorHandler) at bootstrap.
	private readonly injector = inject(Injector);
	private readonly zone = inject(NgZone);
	private readonly sentryHandler = Sentry.createErrorHandler({ showDialog: false });

	handleError(error: unknown): void {
		this.sentryHandler.handleError(error);

		if (isChunkLoadError(error)) {
			const router = this.injector.get(Router);
			const eventId = Sentry.lastEventId();
			this.zone.run(() => {
				router.navigate(["/error"], { queryParams: eventId ? { eventId } : {} });
			});
		}
	}
}
